import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Rss, Calendar, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/pages/Agence";

const FALLBACK_COVER = "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function estimateReadingTime(article) {
  if (article.reading_minutes) return article.reading_minutes;
  const text = (article.content || "").replace(/<[^>]+>/g, " ");
  return Math.max(2, Math.round(text.split(/\s+/).length / 220));
}

export default function Actualites() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Tous");

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    articles.forEach((a) => a.category && set.add(a.category));
    return ["Tous", ...Array.from(set).sort()];
  }, [articles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      const matchesCat = activeCategory === "Tous" || a.category === activeCategory;
      if (!matchesCat) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        (a.excerpt || "").toLowerCase().includes(q) ||
        (a.content || "").toLowerCase().includes(q)
      );
    });
  }, [articles, search, activeCategory]);

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="page-actualites">
      <PageHero
        overline="Actualités · Conseils · Réglementation"
        title="Le journal du Cabinet GPI."
        subtitle="Réglementation, conseils métiers, analyses et informations professionnelles — l'actualité de la copropriété, de l'expertise et de l'immobilier marseillais."
      />

      {/* Filters bar */}
      <section className="py-10 lg:py-14 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                data-testid="actu-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un article, un conseil…"
                className="w-full pl-11 pr-4 py-3 border border-[#E2E8F0] bg-white text-sm focus:border-[#0F172A] outline-none transition-colors"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="actu-categories">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  data-testid={`actu-cat-${c}`}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
                    activeCategory === c
                      ? "bg-[#0F172A] border-[#0F172A] text-white"
                      : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#B8935A] hover:text-[#0F172A]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="lg:ml-auto flex items-center gap-2 text-xs text-[#64748B]">
              <Rss size={14} className="text-[#B8935A]" />
              <span className="font-overline text-[10px]">
                {filtered.length} {filtered.length > 1 ? "articles" : "article"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <SkeletonGrid />
          ) : filtered.length === 0 ? (
            <EmptyState search={search} category={activeCategory} />
          ) : (
            <>
              {/* Featured article */}
              {featured && (
                <Link
                  to={`/actualites/${featured.slug}`}
                  data-testid="actu-featured"
                  className="group grid md:grid-cols-2 gap-8 lg:gap-14 mb-16 bg-white border border-[#E2E8F0] hover:border-[#B8935A] transition-all duration-500 overflow-hidden"
                >
                  <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                    <img
                      src={featured.cover_image || FALLBACK_COVER}
                      alt={featured.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-[#0F172A] text-white text-[10px] font-bold uppercase tracking-wider">
                        À la une
                      </span>
                      <span className="font-overline text-[10px] text-[#B8935A]">
                        {featured.category || "Actualité"}
                      </span>
                    </div>
                    <h2 className="font-serif text-3xl lg:text-4xl text-[#0F172A] mt-6 leading-tight">
                      {featured.title}
                    </h2>
                    <p className="text-[#64748B] mt-5 leading-relaxed">{featured.excerpt}</p>
                    <div className="mt-6 flex items-center gap-5 text-xs text-[#94A3B8]">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} /> {formatDate(featured.created_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} /> {estimateReadingTime(featured)} min
                      </span>
                    </div>
                    <div className="mt-8 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#0F172A] group-hover:text-[#B8935A] transition-colors">
                      Lire l'article
                      <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest */}
              {rest.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                  {rest.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Bottom CTA — FAQ shortcut */}
      <section className="py-16 sm:py-20 bg-[#EFE8DC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-overline-lg text-[#B8935A]">Foire aux questions</span>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#0F172A] mt-4 leading-tight">
            Vous avez une question ?<br />
            <span className="italic">La réponse s'y trouve sûrement déjà.</span>
          </h2>
          <p className="text-[#64748B] mt-5 leading-relaxed max-w-2xl mx-auto">
            Nous avons rassemblé, thème par thème, des réponses claires aux questions
            que les copropriétaires nous posent le plus souvent.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-7">
            {["Syndic", "Syndic en ligne", "AG en visioconférence", "ASL", "Expertise"].map((t) => (
              <Link
                key={t}
                to="/faq"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#B8935A] hover:text-[#0F172A] transition-all"
              >
                {t}
              </Link>
            ))}
          </div>
          <Link to="/faq" className="gpi-btn-primary mt-9" data-testid="actu-faq-cta">
            Consulter toutes les réponses
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function ArticleCard({ article }) {
  return (
    <Link
      to={`/actualites/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className="group bg-white border border-[#E2E8F0] hover:border-[#B8935A] transition-all duration-500 block overflow-hidden"
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={article.cover_image || FALLBACK_COVER}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-7">
        <div className="flex items-center gap-3">
          <span className="font-overline text-[10px] text-[#B8935A]">
            {article.category || "Actualité"}
          </span>
          <span className="text-[10px] text-[#94A3B8]">•</span>
          <span className="text-[10px] text-[#94A3B8] flex items-center gap-1">
            <Clock size={10} /> {estimateReadingTime(article)} min
          </span>
        </div>
        <h3 className="font-serif text-xl text-[#0F172A] mt-3 leading-snug group-hover:text-[#B8935A] transition-colors">
          {article.title}
        </h3>
        <p className="text-[#64748B] text-sm mt-3 leading-relaxed line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
          <span className="text-xs text-[#94A3B8]">{formatDate(article.created_at)}</span>
          <ArrowUpRight size={14} className="text-[#0F172A] group-hover:text-[#B8935A] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
        </div>
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="bg-white border border-[#E2E8F0]">
          <div className="aspect-[4/3] bg-[#F1F5F9] animate-pulse" />
          <div className="p-6">
            <div className="h-3 bg-[#F1F5F9] w-1/3 mb-4 animate-pulse" />
            <div className="h-5 bg-[#F1F5F9] mb-3 animate-pulse" />
            <div className="h-5 bg-[#F1F5F9] w-2/3 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ search, category }) {
  return (
    <div className="text-center py-20" data-testid="actu-empty">
      <Search size={32} className="text-[#B8935A] mx-auto" strokeWidth={1.3} />
      <h3 className="font-serif text-2xl text-[#0F172A] mt-6">Aucun article ne correspond.</h3>
      <p className="text-[#64748B] mt-3 max-w-md mx-auto">
        {search ? (
          <>Aucun résultat pour <strong>« {search} »</strong></>
        ) : category !== "Tous" ? (
          <>Aucun article dans la catégorie <strong>{category}</strong>.</>
        ) : (
          <>Le journal sera mis à jour très prochainement.</>
        )}
      </p>
    </div>
  );
}
